import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

/** Brand colors from `src/styles/_variables.scss`. */
const fp = {
  blue: '#90afe8',
  blueLight: '#a6bce3',
  blueDark: '#819dd0',
  blueNight: '#0c0958',
  orange: '#eaa023',
  orangeDark: '#de9415',
  green: '#5cafa0',
  red: '#ee5551',
  grey: '#ced4da',
  font: '#495057',
  white: '#ffffff',
} as const;

/**
 * Lara (PrimeNG 17 look) tinted with existing $fp-* colors.
 * Not a 1:1 port of fp-theme.scss — leftover PrimeNG 17 selectors stay in that file.
 */
export const FpLara = definePreset(Lara, {
  primitive: {
    borderRadius: {
      sm: '3px',
      md: '3px',
    },
    orange: {
      500: fp.orange,
      600: fp.orangeDark,
      700: fp.orangeDark,
    },
    green: {
      500: fp.green,
      600: fp.green,
    },
    red: {
      500: fp.red,
      400: fp.red,
      600: fp.red,
    },
  },
  semantic: {
    typography: {
      fontFamily:
        "'Lato', sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    primary: {
      50: '#f4f7fc',
      100: '#e8eef8',
      200: '#d1ddf1',
      300: fp.blueLight,
      400: fp.blue,
      500: fp.blue,
      600: fp.blueDark,
      700: '#5a72a8',
      800: fp.blueNight,
      900: '#08063d',
      950: '#04031f',
    },
    formField: {
      paddingX: '0.5rem',
      paddingY: '0.5rem',
      borderRadius: '3px',
      borderColor: fp.grey,
      invalidBorderColor: fp.red,
      color: fp.font,
    },
    colorScheme: {
      light: {
        primary: {
          color: fp.blue,
          inverseColor: fp.white,
          hoverColor: fp.blueDark,
          activeColor: fp.blueNight,
        },
        highlight: {
          background: '#e8eef8',
          focusBackground: '#d1ddf1',
          color: fp.blueNight,
          focusColor: fp.blueNight,
        },
        formField: {
          background: fp.white,
          hoverBorderColor: fp.blue,
          focusBorderColor: fp.blueDark,
        },
        overlay: {
          select: {
            background: fp.white,
            color: fp.font,
            borderColor: 'transparent',
          },
          popover: {
            background: fp.white,
            color: fp.font,
          },
        },
        text: {
          color: fp.font,
        },
      },
    },
    focusRing: {
      color: 'rgba(234, 160, 35, 0.45)',
    },
  },
  components: {
    accordion: {
      panel: {
        borderWidth: '0',
        borderColor: 'transparent',
      },
      header: {
        padding: '0 1rem 0 0',
        fontWeight: '400',
        background: fp.white,
        hoverBackground: fp.white,
        activeBackground: fp.white,
        activeHoverBackground: fp.white,
        color: fp.font,
        hoverColor: fp.font,
        activeColor: fp.font,
        activeHoverColor: fp.font,
        borderWidth: '0',
        borderColor: 'transparent',
        borderRadius: '6px',
        first: {
          borderWidth: '0',
          topBorderRadius: '6px',
        },
        last: {
          bottomBorderRadius: '6px',
          activeBottomBorderRadius: '0',
        },
        toggleIcon: {
          color: '#6c757d',
          hoverColor: '#6c757d',
          activeColor: '#6c757d',
          activeHoverColor: '#6c757d',
        },
      },
      content: {
        borderWidth: '0',
        borderColor: 'transparent',
        background: fp.white,
        padding: '0',
        color: fp.font,
      },
    },
    panelmenu: {
      root: {
        gap: '0',
      },
      panel: {
        background: fp.white,
        borderColor: 'transparent',
        borderWidth: '0',
        padding: '0',
        borderRadius: '0',
        first: {
          borderWidth: '0',
          topBorderRadius: '0',
        },
        last: {
          borderWidth: '0',
          bottomBorderRadius: '0',
        },
      },
      item: {
        padding: '0.75rem 0.75rem 0.75rem 3rem',
        borderRadius: '0',
        gap: '0',
        color: fp.font,
        focusColor: fp.font,
        focusBackground: '#fbf7f3',
        icon: {
          color: '#6c757d',
          focusColor: '#6c757d',
        },
        label: {
          fontWeight: '400',
        },
      },
      submenu: {
        indent: '0',
      },
      submenuIcon: {
        color: '#6c757d',
        focusColor: '#6c757d',
      },
    },
    drawer: {
      root: {
        borderColor: 'transparent',
      },
      header: {
        padding: '1rem',
      },
      content: {
        padding: '0',
      },
    },
    autocomplete: {
      overlay: {
        background: fp.white,
        color: fp.font,
        borderColor: 'transparent',
        borderRadius: '3px',
        shadow:
          '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
      },
    },
    multiselect: {
      overlay: {
        background: fp.white,
        color: fp.font,
        borderColor: 'transparent',
        borderRadius: '3px',
        shadow:
          '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
      },
    },
    select: {
      dropdown: {
        width: '2.357rem',
        color: '#6c757d',
      },
      overlay: {
        background: fp.white,
        color: fp.font,
        borderColor: 'transparent',
        borderRadius: '3px',
        shadow:
          '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
      },
      list: {
        padding: '0.5rem 0',
        gap: '0',
      },
      option: {
        padding: '0.5rem 1rem',
        borderRadius: '0',
        selectedBackground: 'rgba(166, 188, 227, 0.22)',
        selectedColor: fp.font,
        selectedFocusBackground: 'rgba(166, 188, 227, 0.22)',
        selectedFocusColor: fp.font,
        focusBackground: '#e9ecef',
        focusColor: fp.font,
      },
    },
    rating: {
      root: {
        gap: '0.5rem',
      },
      icon: {
        size: '1.143rem',
        color: fp.orange,
        hoverColor: fp.orange,
        activeColor: fp.orange,
      },
    },
    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '700',
        padding: '0.25rem 0.4rem',
        borderRadius: '3px',
      },
      primary: {
        background: fp.blue,
        color: fp.white,
      },
      secondary: {
        background: '#f2ebe5',
        color: fp.font,
      },
      success: {
        background: fp.green,
        color: fp.white,
      },
      info: {
        background: fp.blue,
        color: fp.white,
      },
      warn: {
        background: fp.orange,
        color: '#212529',
      },
      danger: {
        background: fp.red,
        color: fp.white,
      },
    },
    button: {
      root: {
        paddingX: '1rem',
        paddingY: '0.5rem',
        label: {
          fontWeight: '700',
        },
        warn: {
          background: fp.orange,
          hoverBackground: fp.orangeDark,
          activeBackground: fp.orangeDark,
          borderColor: fp.orange,
          hoverBorderColor: fp.orangeDark,
          activeBorderColor: fp.orangeDark,
          color: fp.white,
          hoverColor: fp.white,
          activeColor: fp.white,
        },
      },
    },
  },
});
